import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

/** Keep SVG units equal to CSS pixels so axis labels stay readable on narrow screens. */
export function useChartWidth() {
  const [width, setWidth] = useState(720);
  const ref = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const measure = () => setWidth(Math.max(240, Math.round(node.getBoundingClientRect().width)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/** "Nice" axis ticks: 1/2/5 x 10^n steps covering [min, max]. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!(max > min)) return [min];
  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  // smallest 1/2/2.5/5 step that yields at most count + 2 ticks: dense enough to read values off, never crowded
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => (max - min) / s <= count + 1.5) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-9; v += step) out.push(Number(v.toPrecision(12)));
  return out;
}

/**
 * Crosshair state: the pointer (or arrow keys, for keyboard users) selects the nearest X, never a mark.
 * `xs` are the pixel positions of the data columns inside the SVG viewBox.
 */
export function useCrosshair(xs: number[], viewWidth: number) {
  const [index, setIndex] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement | null>(null);
  const onPointerMove = useCallback((e: PointerEvent<SVGSVGElement>) => {
    const box = ref.current?.getBoundingClientRect();
    if (!box || !xs.length) return;
    const x = ((e.clientX - box.left) / box.width) * viewWidth;
    let best = 0;
    for (let i = 1; i < xs.length; i++) if (Math.abs(xs[i]! - x) < Math.abs(xs[best]! - x)) best = i;
    setIndex(best);
  }, [xs, viewWidth]);
  const onKeyDown = useCallback((e: KeyboardEvent<SVGSVGElement>) => {
    if (!xs.length) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setIndex((i) => Math.min(xs.length - 1, Math.max(0, (i ?? xs.length - 1) + (e.key === 'ArrowRight' ? 1 : -1))));
    } else if (e.key === 'Escape') setIndex(null);
  }, [xs]);
  return { ref, index, onPointerMove, onKeyDown, clear: () => setIndex(null), focus: () => setIndex((i) => i ?? xs.length - 1) };
}
